/**
 * What the basket page makes of a refresh (docs/features/customer-portal-ordering/22 section 9): the notices for the
 * prices that moved. Headless: the server's figures, formatted for reading, and which of them the person has not yet
 * dismissed. Nothing about "the old price" is stored anywhere, so the notices live in the page's state and are gone
 * with it; checkout stays the gate whatever they say.
 */

import type {
  CustomerBasketLine,
  CustomerMovedLinePrice,
} from "../data/customer-basket-types";
import { formatQuotedPrice } from "./formatters";

export interface MovedPriceNotice {
  lineId: string;
  /** The line's title, so the notice says which item. */
  title: string;
  /** The price it was, ex VAT, whole line: "£86.00". */
  was: string;
  /** The price it is now: "£80.00". Rises and falls alike. */
  now: string;
}

/**
 * The notices still to show: one per line whose price moved, in the basket's order, for a line still in the basket
 * and not dismissed. A line the person removed since has nothing to notice.
 */
export function movedPriceNotices(
  changes: readonly CustomerMovedLinePrice[],
  lines: readonly CustomerBasketLine[],
  dismissedLineIds: ReadonlySet<string>,
): MovedPriceNotice[] {
  const changeOf = new Map(changes.map((change) => [change.line_id, change]));
  return lines.flatMap((line) => {
    const change = changeOf.get(line.line_id);
    if (change === undefined || dismissedLineIds.has(line.line_id)) return [];
    return [
      {
        lineId: line.line_id,
        title: line.title,
        was: formatQuotedPrice(change.old_price_pence),
        now: formatQuotedPrice(change.new_price_pence),
      },
    ];
  });
}

/** Whether the page should ask the server to price the basket again: a line is stale and the read names how. */
export function shouldRefreshOnArrival(basket: {
  lines: readonly Pick<CustomerBasketLine, "needs_refresh">[];
  actions: { refresh_url?: string | null };
}): boolean {
  return (
    (basket.actions.refresh_url ?? null) !== null &&
    basket.lines.some((line) => line.needs_refresh)
  );
}
